import { useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Drawer,
  IconButton,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import Logo from "src/components/logo";
import Iconify from "src/components/iconify";
import Scrollbar from "src/components/scrollbar";
import { NavSectionVertical } from "src/components/nav-section";
import { PATH_PARTNER } from "src/routes/paths";
import { partnerLogout } from "src/redux/slices/partnerAuth";
import { RootState } from "src/redux/store";
import navConfig from "./nav/config-navigation";

const DRAWER_WIDTH = 280;

const NAV_ITEMS = [
  {
    title: "Home",
    path: PATH_PARTNER.home,
    icon: "akar-icons:home",
  },
  {
    title: "Clientes",
    path: PATH_PARTNER.clients,
    icon: "humbleicons:users",
  },
  {
    title: "Planos Assinados",
    path: PATH_PARTNER.signatures,
    icon: "solar:document-text-outline",
  },
];

export default function PartnerLayout() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const partnerAuth = useSelector((state: RootState) => state.partnerAuth);
  const [openNav, setOpenNav] = useState(false);

  const currentTitle = useMemo(
    () => NAV_ITEMS.find((item) => pathname.startsWith(item.path))?.title || "Painel do Parceiro",
    [pathname],
  );

  useEffect(() => {
    if (openNav) {
      setOpenNav(false);
    }
  }, [pathname]);

  const handleLogout = () => {
    dispatch(partnerLogout());
    navigate(PATH_PARTNER.login, { replace: true });
  };

  const renderDrawerContent = (
    <Scrollbar
      sx={{
        height: 1,
        "& .simplebar-content": {
          height: 1,
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Stack
        spacing={3}
        sx={{
          pt: 3,
          pb: 2,
          px: 2.5,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{ width: "fit-content", cursor: "pointer" }}
          onClick={() => navigate(PATH_PARTNER.home)}
        >
          <Logo disabledLink />
        </Box>

        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{
            p: 1.5,
            borderRadius: 1.5,
            bgcolor: (themeValue) => themeValue.palette.grey[900],
          }}
        >
          <Avatar sx={{ width: 36, height: 36 }}>
            {(partnerAuth.partner.name || "P").slice(0, 1).toUpperCase()}
          </Avatar>
          <Stack sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" noWrap sx={{ color: "common.white" }}>
              {partnerAuth.partner.name || "Parceiro"}
            </Typography>
            <Typography variant="caption" noWrap sx={{ color: "grey.500" }}>
              {partnerAuth.partner.email || "-"}
            </Typography>
          </Stack>
        </Stack>
      </Stack>

      <NavSectionVertical data={navConfig} />

      {/* <Box sx={{ flexGrow: 1 }} />

      <Stack sx={{ px: 2, pb: 2 }}>
        <Button
          fullWidth
          variant="outlined"
          color="inherit"
          startIcon={<Iconify icon="solar:logout-2-outline" />}
          onClick={handleLogout}
          sx={{
            color: "grey.300",
            borderColor: "grey.700",
            "&:hover": { borderColor: "grey.500", bgcolor: "grey.900" },
          }}
        >
          Sair
        </Button>
      </Stack> */}
    </Scrollbar>
  );

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default", display: "flex" }}>
      <AppBar
        color="default"
        sx={{
          boxShadow: "none",
          borderBottom: (themeValue) => `dashed 1px ${themeValue.palette.divider}`,
          bgcolor: "background.paper",
          width: { lg: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { lg: `${DRAWER_WIDTH}px` },
        }}
      >
        <Toolbar sx={{ px: { xs: 2, md: 3 } }}>
          {!isDesktop && (
            <IconButton onClick={() => setOpenNav(true)} sx={{ mr: 1.5 }}>
              <Iconify icon="eva:menu-2-fill" />
            </IconButton>
          )}

          <Typography variant="h6" noWrap>
            {currentTitle}
          </Typography>

          <Box sx={{ flexGrow: 1 }} />

          <Button
            variant="text"
            color="inherit"
            startIcon={<Iconify icon="solar:logout-2-outline" />}
            onClick={handleLogout}
            sx={{ display: { xs: "none", sm: "inline-flex" } }}
          >
            Sair
          </Button>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{
          width: { lg: DRAWER_WIDTH },
          flexShrink: { lg: 0 },
        }}
      >
        {isDesktop ? (
          <Drawer
            open
            variant="permanent"
            PaperProps={{
              sx: {
                width: DRAWER_WIDTH,
                borderRight: "none",
                bgcolor: "common.black",
              },
            }}
          >
            {renderDrawerContent}
          </Drawer>
        ) : (
          <Drawer
            open={openNav}
            onClose={() => setOpenNav(false)}
            ModalProps={{ keepMounted: true }}
            PaperProps={{
              sx: {
                width: DRAWER_WIDTH,
                bgcolor: "common.black",
              },
            }}
          >
            {renderDrawerContent}
          </Drawer>
        )}
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          px: { xs: 2, md: 3 },
          py: { xs: 10, md: 12 },
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
}
